/// <reference types="vite/client" />
/**
 * Typed client for the admin-cf-metrics Worker.
 * Browser -> Worker only; the CF API token is held by the Worker.
 */

export const CF_METRICS_API: string =
  (import.meta.env.VITE_CF_METRICS_API as string | undefined) ??
  "https://admin-cf-metrics.will-i-bracken.workers.dev";

export type CfWindow = "1h" | "24h" | "7d" | "30d";
export type CfTopDim = "country" | "status" | "host";

export interface CfZone {
  id: string;
  name: string;
}

export interface CfSummary {
  requests: number;
  bandwidth_bytes: number;
  threats: number;
  page_views: number;
  unique_visitors: number;
  cache_hit_ratio: number;
}

export interface CfTimeseriesPoint {
  ts: string;
  requests: number;
  bandwidth: number;
  threats: number;
}

export interface CfTopRow {
  key: string;
  requests: number;
}

async function getJSON<T>(path: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(`${CF_METRICS_API}${path}`, { signal });
  if (!r.ok) {
    let detail = "";
    try {
      const j = (await r.json()) as { error?: string };
      detail = j.error ? `: ${j.error}` : "";
    } catch {
      /* ignore */
    }
    throw new Error(`cf-metrics ${r.status}${detail}`);
  }
  return (await r.json()) as T;
}

export async function listZones(signal?: AbortSignal): Promise<CfZone[]> {
  const j = await getJSON<{ zones: CfZone[] }>("/api/cf/zones", signal);
  return j.zones;
}

export async function fetchSummary(
  zoneId: string,
  since: CfWindow,
  signal?: AbortSignal,
): Promise<CfSummary> {
  return await getJSON<CfSummary>(
    `/api/cf/zone-summary?zoneId=${encodeURIComponent(zoneId)}&since=${since}`,
    signal,
  );
}

export async function fetchTimeseries(
  zoneId: string,
  since: CfWindow,
  interval: string,
  signal?: AbortSignal,
): Promise<CfTimeseriesPoint[]> {
  const j = await getJSON<{ points: CfTimeseriesPoint[] }>(
    `/api/cf/zone-timeseries?zoneId=${encodeURIComponent(zoneId)}&since=${since}&interval=${interval}`,
    signal,
  );
  return j.points;
}

export async function fetchTop(
  zoneId: string,
  since: CfWindow,
  dim: CfTopDim,
  limit = 10,
  signal?: AbortSignal,
): Promise<CfTopRow[]> {
  const j = await getJSON<{ rows: CfTopRow[]; dim: CfTopDim }>(
    `/api/cf/zone-top?zoneId=${encodeURIComponent(zoneId)}&dim=${dim}&limit=${limit}&since=${since}`,
    signal,
  );
  return j.rows;
}

export function formatBytes(n: number): { value: number; unit: string } {
  if (!Number.isFinite(n) || n <= 0) return { value: 0, unit: "B" };
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return { value: Math.round(v * 10) / 10, unit: units[i] };
}
